/**
 * Pure colour maths for the banking kit (no React, no theme). Every input comes
 * from the tenant branding record via `getColor(...)` — this file only mixes it.
 *
 * Chakra's `colorScheme` needs a 50–900 palette of REAL colours (its theme tools
 * parse them to make dark-mode tints), so a brand hex is expanded here into one,
 * rather than handing Chakra a `color-mix(...)` string it cannot parse.
 */

export type Palette = Record<50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900, string>;

type Rgb = [number, number, number];

/** "#38f" / "#3388ff" / "rgb(51, 136, 255)" / "rgba(…)" → [r, g, b]; anything else → null. */
export function parseColor(input: string | null | undefined): Rgb | null {
  if (!input) return null;
  const s = input.trim();
  const hex = s.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hex) {
    const h = hex[1].length === 3 ? hex[1].split("").map((c) => c + c).join("") : hex[1];
    return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
  }
  const rgb = s.match(/^rgba?\(\s*(\d{1,3})[\s,]+(\d{1,3})[\s,]+(\d{1,3})/i);
  if (rgb) return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];
  return null;
}

const toHex = ([r, g, b]: Rgb) =>
  "#" + [r, g, b].map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0")).join("");

/** `color` moved `amount` (0–1) of the way toward `target`. */
export function mix(color: string, target: "white" | "black", amount: number): string {
  const c = parseColor(color);
  if (!c) return color;
  const t = target === "white" ? 255 : 0;
  return toHex([c[0] + (t - c[0]) * amount, c[1] + (t - c[1]) * amount, c[2] + (t - c[2]) * amount]);
}

/**
 * A Chakra palette whose 500 is the brand colour itself. Lighter steps mix toward
 * white, darker toward black. Returns null when the colour cannot be parsed, so
 * the caller can fall back to one of Chakra's own palettes instead of guessing.
 */
export function paletteFrom(base: string | null | undefined): Palette | null {
  const c = parseColor(base);
  if (!c) return null;
  const hex = toHex(c);
  return {
    50: mix(hex, "white", 0.92),
    100: mix(hex, "white", 0.82),
    200: mix(hex, "white", 0.62),
    300: mix(hex, "white", 0.42),
    400: mix(hex, "white", 0.2),
    500: hex,
    600: mix(hex, "black", 0.16),
    700: mix(hex, "black", 0.32),
    800: mix(hex, "black", 0.48),
    900: mix(hex, "black", 0.62),
  };
}

/**
 * The colour to WRITE WITH (text, icons, amounts) on the page background.
 * Brand colours are chosen for fills; on white many are too pale to read
 * (Mac green on white is about 2:1). Light mode darkens toward black, dark mode
 * lightens a touch — same hue, readable contrast, still the tenant's colour.
 */
export function ink(color: string, mode: "light" | "dark"): string {
  return mode === "light" ? mix(color, "black", 0.38) : mix(color, "white", 0.18);
}

/** `color` at `amount` (0–1) opacity, for tints behind text. Works for any CSS colour. */
export const alpha = (color: string, amount: number) => `color-mix(in srgb, ${color} ${Math.round(amount * 100)}%, transparent)`;
